import type { TopLevelSpec } from "vega-lite";
import type { BaseChartSpecOptions, FieldType } from "../types.js";
import {
  numberFormatConfig,
  numberFormatProps,
  type NumberFormat,
} from "./format.js";
import {
  buildTitle,
  categoryAxisLabels,
  fieldRef,
  inlineData,
} from "./shared.js";

export type Interpolate = "linear" | "monotone" | "step" | "basis" | "cardinal";

export interface LineChartSpecOptions extends BaseChartSpecOptions {
  /** Field for the x axis (defaults to a temporal scale). */
  x: string;
  /** Field for the y axis. */
  y: string;
  xType?: FieldType;
  yType?: FieldType;
  /** Field that splits the data into multiple colored lines. */
  color?: string;
  colorType?: FieldType;
  /**
   * Draw a marker at each data point. Without one, the points are still
   * there to hover for their tooltip, just not drawn.
   */
  point?: boolean;
  /** Curve interpolation between points. */
  interpolate?: Interpolate;
  xTitle?: string;
  yTitle?: string;
  /** Fill the area under the line (an area chart). */
  filled?: boolean;
  /**
   * Keep a discrete x axis in the order of the data (the caller ordered it)
   * rather than sorting it by name.
   */
  keepOrder?: boolean;
  /** Whether the value axis starts at zero. On by default. */
  zero?: boolean;
  /** How values read on the value axis and the tooltip. */
  valueFormat?: NumberFormat;
  /** How values read on the value axis, when terser than `valueFormat` (compact currency). */
  axisFormat?: NumberFormat;
  /** Gridlines across the value axis. On by default. */
  grid?: boolean;
}

/** Builds a Vega-Lite line chart spec. */
export function lineChartSpec(options: LineChartSpecOptions): TopLevelSpec {
  const {
    data,
    x,
    y,
    color,
    colorType = "nominal",
    xType = "temporal",
    yType = "quantitative",
    point = false,
    interpolate = "linear",
    xTitle,
    yTitle,
    title,
    subtitle,
    filled = false,
    keepOrder = false,
    zero = true,
    valueFormat,
    axisFormat = valueFormat,
    grid = true,
  } = options;

  const titleValue = buildTitle(title, subtitle);
  const xEnc = {
    field: fieldRef(x),
    type: xType,
    ...(xTitle !== undefined ? { title: xTitle } : {}),
    ...(keepOrder ? { sort: null } : {}),
    ...(xType === "nominal" || xType === "ordinal"
      ? { axis: categoryAxisLabels(data, x) }
      : {}),
  };
  const yEnc = {
    field: fieldRef(y),
    type: yType,
    ...(yTitle !== undefined ? { title: yTitle } : {}),
    ...(zero ? {} : { scale: { zero: false } }),
    axis: { grid, ...numberFormatProps(axisFormat) },
  };
  const colorField = color ? { field: fieldRef(color), type: colorType } : null;
  // "transparent" points are hoverable but not drawn, so every point still
  // has a tooltip.
  const points = point ? true : "transparent";

  return {
    data: inlineData(data),
    mark: filled
      ? { type: "area", line: true, point: points, interpolate, opacity: 0.2 }
      : { type: "line", point: points, interpolate },
    encoding: {
      x: xEnc,
      y: yEnc,
      ...(colorField ? { color: colorField } : {}),
      tooltip: [
        { field: xEnc.field, type: xEnc.type, title: xEnc.title },
        ...(colorField ? [colorField] : []),
        {
          field: yEnc.field,
          type: yEnc.type,
          title: yEnc.title,
          ...numberFormatProps(valueFormat),
        },
      ],
    },
    ...(titleValue !== undefined ? { title: titleValue } : {}),
    ...numberFormatConfig(valueFormat, axisFormat),
  };
}
